import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { api } from '@/api/client';
import type { BreakdownRow, OverviewResponse } from '@/api/types';
import { metricValue, resolveMetric } from '@/api/metric-resolution';

export const RANGES = [
  { label: 'Last 7 days', days: 7 },
  { label: 'Last 30 days', days: 30 },
  { label: 'Last 90 days', days: 90 },
] as const;

export const DEFAULT_DAYS = 30;

/**
 * The single query behind every tab.
 *
 * All four tabs call this with the same arguments, so TanStack dedupes them to ONE network
 * request — switching tabs is instant and costs nothing. The range lives in the URL (`?days=`)
 * so it survives a tab change, a refresh, and a shared link.
 */
export function useOverview(siteKey: string) {
  const range = useDateRange();

  const query = useQuery({
    queryKey: ['overview', siteKey, range.from, range.to],
    queryFn: () => api.overview(siteKey, range.from, range.to),
  });

  return { ...query, ...range };
}

/**
 * The range half of {@link useOverview}, on its own.
 *
 * Extracted so a tab that does NOT read the overview (Leads) still shares one definition of what
 * `?days` / `?from` / `?to` mean. Two implementations would drift, and the symptom would be a
 * range that changes meaning when you switch tabs.
 */
export function useDateRange() {
  const [params, setParams] = useSearchParams();

  const days = Number(params.get('days')) || DEFAULT_DAYS;

  // Explicit dates win over the preset. Both live in the URL so a range is shareable and
  // survives a refresh — the same reason `days` is there.
  const explicitFrom = params.get('from');
  const explicitTo = params.get('to');

  const setDays = (value: number) => {
    const next = new URLSearchParams(params);
    next.set('days', String(value));
    // Choosing a preset must clear a custom range, or the preset silently does nothing.
    next.delete('from');
    next.delete('to');
    setParams(next, { replace: true });
  };

  /** Sets an explicit range. Pass nulls to return to the preset. */
  const setRange = (fromDate: string | null, toDate: string | null) => {
    const next = new URLSearchParams(params);
    if (fromDate && toDate) {
      // Tolerate reversed input rather than returning an empty result the user cannot explain.
      const [lo, hi] = fromDate <= toDate ? [fromDate, toDate] : [toDate, fromDate];
      next.set('from', lo);
      next.set('to', hi);
    } else {
      next.delete('from');
      next.delete('to');
    }
    setParams(next, { replace: true });
  };

  const { from, to } = useMemo(() => {
    if (explicitFrom && explicitTo) {
      return { from: explicitFrom, to: explicitTo };
    }

    const end = new Date();
    // Yesterday is the last COMPLETE day; today is included only when it has data, and it is
    // flagged provisional by the API.
    const start = new Date(end);
    start.setUTCDate(start.getUTCDate() - (days - 1));
    return { from: iso(start), to: iso(end) };
  }, [days, explicitFrom, explicitTo]);

  const isCustomRange = Boolean(explicitFrom && explicitTo);

  return { days, setDays, from, to, setRange, isCustomRange };
}

/** Reads a breakdown, returning [] rather than undefined so pages need no guards. */
export function breakdown(data: OverviewResponse | undefined, dimension: string): BreakdownRow[] {
  return data?.breakdowns[dimension] ?? [];
}

/**
 * Ranks a breakdown row by its session count, GA4 first.
 *
 * Precedence lives in `MetricChains`, not in an inline `??` here — both providers write into the
 * same metrics dictionary, so this function decides which one the whole dashboard believes. The
 * previous inline chain also fell through to `active_users`, silently ranking rows by USERS when
 * neither session metric was present, which is a different measure wearing the same label.
 */
export function rowValue(row: BreakdownRow): number {
  return metricValue(row.metrics, 'sessions') ?? 0;
}

/** Which provider supplied a row's session figure — for the provenance line in the UI. */
export function rowSource(row: BreakdownRow) {
  return resolveMetric(row.metrics, 'sessions');
}

export function toBars(rows: BreakdownRow[]) {
  return rows.map((row) => ({ label: row.value || '(not set)', value: rowValue(row) }));
}

function iso(date: Date): string {
  return date.toISOString().slice(0, 10);
}
